// Returns a YYYY-MM-DD string in LOCAL time (avoids UTC midnight shift)
export function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Converts a Google Calendar event to a task object
export function calendarEventToTask(ev) {
  return {
    id: `google_${ev.id}`, // Prefix to avoid ID conflicts
    title: ev.summary || "Untitled event",
    date: ev.start.dateTime
      ? ev.start.dateTime.slice(0, 10)
      : ev.start.date,
    time: ev.start.dateTime
      ? ev.start.dateTime.slice(11, 16)
      : "09:00",
    deadline: ev.end?.dateTime
      ? ev.end.dateTime
      : null,
    details: ev.description || "",
    priority: "medium", // Default priority for calendar events
    external: true, // Mark as from Google Calendar
    editable: false, // Don't allow editing in Aidey
    completed: false,
    category: "calendar", // Special category for calendar events
    originalEvent: ev, // Keep reference to original event
  };
}

// Create Google Calendar event from Aidey task
export async function createGoogleEventFromTask(task, gapi, includeGoogleMeet = false) {
  if (!gapi || !gapi.client || !gapi.client.calendar) {
    console.error("Google Calendar API not initialized");
    return false;
  }

  // Calculate end time (default to 1 hour after start)
  const startTime = task.time || "09:00";
  const startHour = parseInt(startTime.slice(0, 2));
  const startMinute = startTime.slice(3);
  const endHour = (startHour + 1) % 24;
  const endTime = `${endHour.toString().padStart(2, "0")}:${startMinute}`;

  const event = {
    summary: task.title,
    description: task.details || "",
    start: {
      dateTime: `${task.date}T${startTime}:00`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    end: {
      dateTime: `${task.date}T${endTime}:00`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
  };

  // Add Google Meet conference if requested
  if (includeGoogleMeet) {
    event.conferenceData = {
      createRequest: {
        requestId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        conferenceSolutionKey: {
          type: "hangoutsMeet"
        }
      }
    };
  }

  try {
    const insertParams = {
      calendarId: 'primary',
      resource: event,
    };

    // Add conferenceDataVersion if creating a Google Meet
    if (includeGoogleMeet) {
      insertParams.conferenceDataVersion = 1;
    }

    const response = await gapi.client.calendar.events.insert(insertParams);
    console.log("Successfully added to Google Calendar:", response);
    
    // Return the event with Google Meet link if available
    const createdEvent = response.result;
    const result = { success: true, event: createdEvent };
    
    if (createdEvent.conferenceData && createdEvent.conferenceData.entryPoints) {
      const meetLink = createdEvent.conferenceData.entryPoints.find(ep => ep.entryPointType === 'video');
      if (meetLink) {
        result.googleMeetLink = meetLink.uri;
      }
    }
    
    return result;
  } catch (err) {
    console.error("Failed to add to Google Calendar:", err);
    return { success: false, error: err };
  }
}

// Merge Aidey tasks with Google Calendar events, iCal events, and Outlook events for a specific date
export function getAllTasksForDate(aideyTasks, googleEvents, dateStr, icalEvents = [], outlookEvents = []) {
  // Aidey tasks for this date — check task.date first, fall back to task.datetime
  const localTasks = aideyTasks.filter(t => {
    if (t.completed) return false;
    const taskDate = t.date
      || (t.datetime ? toLocalDateStr(new Date(t.datetime)) : null);
    return taskDate === dateStr;
  });

  // Google events for this date, mapped to tasks
  const googleTasks = (googleEvents || [])
    .filter(ev => {
      if (!ev.start) return false;
      // Handle all-day vs timed events
      const eventDate = ev.start.dateTime
        ? ev.start.dateTime.slice(0, 10)
        : ev.start.date;
      return eventDate === dateStr;
    })
    .map(calendarEventToTask);

  // iCal events for this date
  const icalTasks = (icalEvents || []).filter(task => task.date === dateStr);

  // Outlook events for this date (already in task format)
  const outlookTasks = (outlookEvents || []).filter(task => task.date === dateStr);

  // Combine and sort by time
  const allTasks = [...localTasks, ...googleTasks, ...icalTasks, ...outlookTasks];
  
  // Sort by time, putting timed tasks first, then all-day tasks
  return allTasks.sort((a, b) => {
    if (a.time && b.time) {
      return a.time.localeCompare(b.time);
    }
    if (a.time && !b.time) return -1;
    if (!a.time && b.time) return 1;
    return 0;
  });
}

// Get all tasks for a date range (useful for week/month views)
export function getAllTasksForDateRange(aideyTasks, googleEvents, startDate, endDate, icalEvents = [], outlookEvents = []) {
  const allTasks = [];
  
  // Convert date strings to Date objects for comparison
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Generate all dates in the range
  const currentDate = new Date(start);
  while (currentDate <= end) {
    const dateStr = toLocalDateStr(currentDate);
    const tasksForDate = getAllTasksForDate(aideyTasks, googleEvents, dateStr, icalEvents, outlookEvents);
    allTasks.push(...tasksForDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return allTasks;
}

// Check if a task is from Google Calendar
export function isGoogleCalendarTask(task) {
  return task.external === true && (task.category === "calendar" || task.id?.startsWith('google_'));
}

// Check if a task is from iCal
export function isICalTask(task) {
  return task.external === true && (task.category === "ical" || task.id?.startsWith('ical_') || task.source === "iCal");
}

// Check if a task is from Outlook
export function isOutlookTask(task) {
  return task.external === true && (task.category === "outlook" || task.id?.startsWith('outlook_') || task.source === "Outlook");
}

// Check if a task is from any external calendar
export function isExternalCalendarTask(task) {
  return isGoogleCalendarTask(task) || isICalTask(task) || isOutlookTask(task);
}
